import React, { useEffect, useMemo, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { apiError, ui } from "../kits/shared";
import { sortSizes } from "../products/variantes";
import MatrizCantidades, { cellKey, cellLabel } from "../warehouses/matrizCantidades";

/*
	Comprar un producto con tallas/colores de una vez (Esteban, 2026-10-29: "siempre debe preguntar cuántos de c/u, en general
	se compran de varias tallas"). Cada casilla con piezas es un renglón de la orden; si esa talla ya estaba, se le suman.
*/

export interface PickedPiece { articleId: number; name: string; waCode: string | null; quantity: number }

const ComprarPorTallas: React.FC<{
	campusId: number; modelId: number; currencyCode: string;
	/** Lo que la orden ya trae de este producto, por articleId. */ inOrder: Record<number, number>;
	onAdd: (pieces: PickedPiece[], unitCost: string) => void; onCancel: () => void;
}> = ({ campusId, modelId, currencyCode, inOrder, onAdd, onCancel }) => {
	const http = HttpManager.getInstance().inventoryService;
	const [product, setProduct] = useState<any | null>(null);
	const [scale, setScale] = useState<string[]>([]);
	const [qty, setQty] = useState<Record<string, string>>({});
	const [unitCost, setUnitCost] = useState<string>("");
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		http.get(campusId, modelId).then((response) => {
			setProduct(response.data);
			http.catalogs(campusId).then((catalogs) => setScale(((catalogs.data.sizeTypes || {})[response.data.sizeType || "none"]?.values || []).map((entry: any) => String(entry.value)))).catch(() => setScale([]));
		}).catch((requestError) => setError(apiError(requestError, "No se pudo abrir el producto.")));
	}, [campusId, modelId]);

	const variants: any[] = (product?.variants || []).filter((variant: any) => !variant.deprecatedAt);
	const sizes = sortSizes(scale, Array.from(new Set(variants.map((variant) => variant.sizeValue).filter(Boolean) as string[])));
	const colors = Array.from(new Set(variants.map((variant) => variant.colorValue).filter(Boolean) as string[]));
	const byKey = useMemo(() => {
		const map: Record<string, any> = {};
		variants.forEach((variant) => { map[cellKey(variant.colorValue || null, variant.sizeValue || null)] = variant; });
		return map;
	}, [product]);
	const missing = useMemo(() => {
		const out = new Set<string>();
		(sizes.length ? sizes : [null]).forEach((size) => (colors.length ? colors : [null]).forEach((color) => { const key = cellKey(color, size); if (!byKey[key]) out.add(key); }));
		return out;
	}, [byKey, scale]);
	const hints = useMemo(() => {
		const out: Record<string, string> = {};
		Object.entries(byKey).forEach(([key, variant]) => { if (inOrder[variant.id]) out[key] = "ya " + inOrder[variant.id]; });
		return out;
	}, [byKey, inOrder]);

	const pieces: PickedPiece[] = Object.entries(qty).filter(([key, value]) => Number(value) > 0 && byKey[key]).map(([key, value]) => {
		const variant = byKey[key];
		const label = cellLabel({ size: variant.sizeValue || null, color: variant.colorValue || null });
		return { articleId: variant.id, name: variant.name.includes(label) ? variant.name : product.name + " · " + label, waCode: variant.waCode, quantity: Number(value) };
	});
	const total = pieces.reduce((sum, piece) => sum + piece.quantity, 0);

	return <div style={{ position: "fixed", inset: 0, zIndex: 2300, background: "var(--wa-papel)", overflowY: "auto", display: "flex", flexDirection: "column" }}>
		<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", padding: "10px 14px", background: "var(--wa-superficie)", borderBottom: "1px solid var(--wa-filo)", position: "sticky", top: 0, zIndex: 3 }}>
			<div style={{ minWidth: 0 }}>
				<div style={{ fontWeight: 700, fontSize: "16px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{product ? product.name : "Cargando…"}</div>
				<div style={ui.small}>¿Cuántas de Cada Talla y Color?</div>
			</div>
			<button type="button" className="btn btn-outline-secondary rounded-pill" style={{ flex: "0 0 auto" }} onClick={onCancel}>Cancelar</button>
		</div>
		<div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: "12px", maxWidth: "720px", width: "100%", margin: "0 auto" }}>
			{product && variants.length < 1 ? <div style={ui.small}>Este producto todavía no tiene tallas ni colores. Dalas de alta en Productos.</div> : null}
			{product && variants.length > 0 ? <MatrizCantidades sizes={sizes} colors={colors} qty={qty} onChange={setQty} hints={hints} missing={missing} ordering /> : null}
			{product && variants.length > 0 ? <div style={ui.field("220px")}>
				<span style={ui.caption}>Costo unitario ({currencyCode})</span>
				<input type="number" min={0} step="0.01" className="form-control" style={ui.input} value={unitCost} onChange={(event) => setUnitCost(event.target.value)} />
				<span style={ui.tiny}>Va en todas las tallas; si una cuesta distinto, cámbialo en su renglón.</span>
			</div> : null}
			{error ? <div className="alert alert-danger" style={{ padding: "8px 12px", fontSize: "14px", margin: 0 }}>{error}</div> : null}
			<button type="button" className="btn btn-primary rounded-pill" style={{ height: "42px", fontSize: "15px" }} disabled={!product || pieces.length < 1} onClick={() => onAdd(pieces, unitCost)}>
				{pieces.length < 1 ? "Agregar a la Orden" : "Agregar " + (total === 1 ? "1 Pieza" : total + " Piezas") + " a la Orden"}</button>
		</div>
	</div>;
};

export default ComprarPorTallas;
